import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FormControl as Input, Pagination, Message, Modal, Button } from 'tinper-bee';
import axios from 'axios';
import * as util from '../../utils';
import SkillCard, { formatNumber } from './SkillCard';
import './index.less';
import Layout from '../Layout';

interface Skill {
  id: string;
  name: string;
  version: string;
  description: string;
  skillDescription?: string;
  author?: string;
  creatorName?: string;
  download?: number;
  skill_active_count?: number;
  downloads?: number;
  tags?: string[];
  latestTime?: string;
  [key: string]: any;
}

type MainTab = 'list' | 'guide';
type GuideTab = 'install' | 'manage' | 'publish';
type RankingTab = 'download' | 'active';
type RecommendFilter = 'all' | '效能龙' | '交付龙' | '价值龙';

const PAGE_SIZE = 10;

const SkillMarket: React.FC = () => {
  const [mainTab, setMainTab] = useState<MainTab>('list');
  const [guideTab, setGuideTab] = useState<GuideTab>('install');
  const [rankingTab, setRankingTab] = useState<RankingTab>('download');
  const [recommendFilter, setRecommendFilter] = useState<RecommendFilter>('all');
  const [sortMode, setSortMode] = useState<'recommend' | 'download' | 'active' | 'latest'>('recommend');

  const [skills, setSkills] = useState<Skill[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [hotDownloadData, setHotDownloadData] = useState<Skill[]>([]);
  const [hotActiveData, setHotActiveData] = useState<Skill[]>([]);

  const [skillSearchTags, setSkillSearchTags] = useState<{title: string; value: string}[]>([]);
  const [activeTag, setActiveTag] = useState<string>('');

  const [skillUploadVisible, setSkillUploadVisible] = useState(false);
  const [skillFile, setSkillFile] = useState<File | null>(null);
  const [skillFileName, setSkillFileName] = useState('');
  const [skillUploading, setSkillUploading] = useState(false);
  const skillFileInputRef = useRef<HTMLInputElement>(null);

  const isLoggedin = !!window.localStorage.user;

  const resetSkillFileInput = () => {
    if (skillFileInputRef.current) skillFileInputRef.current.value = '';
    setSkillFile(null);
    setSkillFileName('');
  };

  const onSkillFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setSkillFile(file);
      setSkillFileName(file.name);
    }
  };

  const skillUpload = async () => {
    if (!skillFile) {
      Message.create({ content: '请先选择 zip 文件', color: 'warning' });
      return;
    }
    setSkillUploading(true);
    const formData = new FormData();
    formData.append('file', skillFile);
    try {
      const res = await axios.post('/api/package/skillUpload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });
      const { status, message: msg, data } = res.data;
      if (status === 200) {
        Message.create({ content: `发布成功：${data.name}@${data.version}`, color: 'success' });
        resetSkillFileInput();
        setSkillUploadVisible(false);
        fetchSkills(1, '');
      } else if (status === 207) {
        Message.create({ content: msg, color: 'warning' });
        resetSkillFileInput();
        setSkillUploadVisible(false);
        fetchSkills(1, '');
      } else {
        Message.create({ content: msg || '发布失败', color: 'danger' });
      }
    } catch (e: any) {
      let errMsg = '上传失败';
      if (e.code === 'ECONNABORTED') {
        errMsg = '上传超时，请检查文件大小或网络后重试';
      } else if (e.response && e.response.data) {
        errMsg = e.response.data.message || `服务端错误 (${e.response.status})`;
      } else if (e.message) {
        errMsg = e.message;
      }
      Message.create({ content: errMsg, color: 'danger' });
    } finally {
      setSkillUploading(false);
    }
  };

  useEffect(() => {
    fetchSkills(1, '');
    // 获取搜索标签
    util.get('/home/getFilter').then((res: any) => {
      if (res?.data?.skillSearchTag) {
        setSkillSearchTags(res.data.skillSearchTag);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchRanking(rankingTab);
  }, [rankingTab]);

  const fetchSkills = async (
    page: number = pageNumber,
    search: string = searchValue,
    filter: RecommendFilter = recommendFilter,
    sort: string = sortMode,
    tag: string = activeTag
  ) => {
    setLoading(true);
    try {
      const response = await util.get('/package/list', {
        isSkillPublish: 1,
        search: search || undefined,
        searchTag: tag || undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        // 全部技能沿用市场原有热度排序；仅筛选具体赛道时才启用优选排序。
        hotOrNew: 'hot',
        skillRecommend: filter === 'all' ? undefined : filter,
        sort: filter === 'all' && sort === 'recommend' ? undefined : sort
      });
      if (response && response.data) {
        setSkills(response.data.list || []);
        setTotalCount(response.data.pageInfo?.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch skills:', error);
      Message.create({ content: '获取技能列表失败', color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const changeRecommendFilter = (value: RecommendFilter) => {
    const nextSortMode = value === 'all' ? 'recommend' : sortMode;
    setRecommendFilter(value);
    if (value === 'all') setSortMode('recommend');
    setPageNumber(1);
    fetchSkills(1, searchValue, value, nextSortMode);
  };

  const changeSortMode = (value: any) => {
    setSortMode(value);
    setPageNumber(1);
    fetchSkills(1, searchValue, recommendFilter, value);
  };

  const handleTagClick = (tag: string) => {
    const next = activeTag === tag ? '' : tag;
    setActiveTag(next);
    setPageNumber(1);
    fetchSkills(1, searchValue, recommendFilter, sortMode, next);
  };

  const fetchRanking = async (tab: RankingTab) => {
    try {
      const response = await util.get('/package/list', {
        isSkillPublish: 1,
        pageNumber: 1,
        pageSize: 10,
        sort: tab
      });
      if (response?.data) {
        const list = response.data.list || [];
        if (tab === 'download') {
          setHotDownloadData(list);
        } else {
          setHotActiveData(list);
        }
      }
    } catch (error) {
      console.error('Failed to fetch ranking:', error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
    fetchSkills(1, value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(searchValue);
    }
  };

  const pageChange = (page: number) => {
    setPageNumber(page);
    fetchSkills(page);
    window.scrollTo(0, 0);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      Message.create({ content: '已复制到剪贴板', color: 'success' });
    }).catch(() => {
      Message.create({ content: '复制失败', color: 'danger' });
    });
  };

  const currentRankingData = rankingTab === 'download' ? hotDownloadData : hotActiveData;

  return (
    <div className="skill-market-page">
      <div className="skill-market-container wrap">

        {/* Main Tabs */}
        <div className="main-tabs">
          <button
            className={`main-tab ${mainTab === 'list' ? 'active' : ''}`}
            onClick={() => setMainTab('list')}
          >
            Skill 列表 ({totalCount})
          </button>
          <button
            className={`main-tab ${mainTab === 'guide' ? 'active' : ''}`}
            onClick={() => setMainTab('guide')}
          >
            使用指南
          </button>
        </div>

        <div className="content-layout">
          {/* Left Area */}
          <div className="content-left">
            {mainTab === 'list' && (
              <>
                {/* Search */}
                <div className="search-bar">
                  <div className="search-input-wrap">
                    <span className="search-icon">🔍</span>
                    <Input
                      placeholder="搜索技能名称..."
                      value={searchValue}
                      onChange={(e: any) => handleSearch(e)}
                      onKeyDown={handleSearchKeyDown}
                      className="search-input"
                    />
                  </div>
                  <Button
                    colors="primary"
                    disabled={!isLoggedin}
                    style={{ marginLeft: 12, flexShrink: 0 }}
                    onClick={() => isLoggedin && setSkillUploadVisible(true)}
                  >
                    发布 Skill
                  </Button>
                </div>

                <Modal
                  show={skillUploadVisible}
                  onHide={() => { resetSkillFileInput(); setSkillUploadVisible(false); }}
                  width={500}
                >
                  <Modal.Header closeButton closeButtonProps={{ fieldId: 'closeBtn' }}>
                    <Modal.Title>发布 Skill</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div style={{ marginBottom: 16 }}>
                      <input
                        ref={skillFileInputRef}
                        type="file"
                        accept=".zip"
                        style={{ display: 'none' }}
                        onChange={onSkillFileChange}
                      />
                      <Button
                        bordered
                        onClick={() => skillFileInputRef.current && skillFileInputRef.current.click()}
                        style={{ marginRight: 12 }}
                      >
                        选择文件
                      </Button>
                      <span style={{ color: skillFileName ? '#333' : '#999' }}>
                        {skillFileName || '未选择任何文件（仅支持 .zip）'}
                      </span>
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button onClick={() => { resetSkillFileInput(); setSkillUploadVisible(false); }} colors="secondary" style={{ marginRight: 8 }}>取消</Button>
                    <Button colors="primary" disabled={skillUploading} onClick={skillUpload}>
                      {skillUploading ? '上传中...' : '上传发布'}
                    </Button>
                  </Modal.Footer>
                </Modal>

                <div className="skill-filter-bar">
                  <div className="recommend-filters">
                    <span className="filter-caption">龙王甄选</span>
                    {(['all', '效能龙', '交付龙', '价值龙'] as RecommendFilter[]).map(item => (
                      <button
                        key={item}
                        className={`recommend-filter ${recommendFilter === item ? 'active' : ''} ${item === 'all' ? 'all' : item === '交付龙' ? 'delivery' : item === '价值龙' ? 'value' : 'efficiency'}`}
                        onClick={() => changeRecommendFilter(item)}
                      >
                        {item === 'all' ? '全部' : item}
                      </button>
                    ))}
                  </div>
                  <select className="skill-sort-select" value={sortMode} onChange={e => changeSortMode(e.target.value)}>
                    <option value="recommend">综合推荐</option>
                    <option value="download">下载量</option>
                    <option value="active">激活量</option>
                    <option value="latest">最新发布</option>
                  </select>
                </div>

                {skillSearchTags.length > 0 && (
                  <div className="skill-search-tags">
                    {skillSearchTags.map(tag => (
                      <button
                        key={tag.value}
                        className={`skill-search-tag ${activeTag === tag.value ? 'active' : ''}`}
                        onClick={() => handleTagClick(tag.value)}
                      >
                        {tag.title}
                      </button>
                    ))}
                  </div>
                )}

                {/* Skill Grid */}
                {loading ? (
                  <div className="skill-loading">
                    <div className="loading-spinner"></div>
                    <p>加载中...</p>
                  </div>
                ) : skills.length > 0 ? (
                  <>
                    <div className="skill-grid">
                      {skills.map((skill, index) => (
                        <SkillCard key={skill.id} skill={skill} index={index} />
                      ))}
                    </div>
                    {totalCount > PAGE_SIZE && (
                      <div className="pagination-wrapper">
                        <Pagination
                          first
                          last
                          prev
                          next
                          maxButtons={5}
                          boundaryLinks
                          activePage={pageNumber}
                          onSelect={pageChange}
                          showJump={true}
                          total={totalCount}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="skill-empty">
                    <div className="empty-icon">🔍</div>
                    <p className="empty-text">
                      {searchValue ? `未找到与 "${searchValue}" 相关的技能` : '暂无技能'}
                    </p>
                    <p className="empty-hint">
                      {searchValue ? '尝试使用其他关键词搜索' : '稍后再来看看'}
                    </p>
                  </div>
                )}
              </>
            )}

            {mainTab === 'guide' && (
              <div className="guide-section">
                <div className="guide-tabs">
                  <button
                    className={`guide-tab ${guideTab === 'install' ? 'active' : ''}`}
                    onClick={() => setGuideTab('install')}
                  >
                    <span className="tab-icon">📥</span>
                    <span>安装技能</span>
                  </button>
                  <button
                    className={`guide-tab ${guideTab === 'manage' ? 'active' : ''}`}
                    onClick={() => setGuideTab('manage')}
                  >
                    <span className="tab-icon">🔧</span>
                    <span>管理技能</span>
                  </button>
                  <button
                    className={`guide-tab ${guideTab === 'publish' ? 'active' : ''}`}
                    onClick={() => setGuideTab('publish')}
                  >
                    <span className="tab-icon">📤</span>
                    <span>发布技能</span>
                  </button>
                </div>

                <div className="guide-content">
                  {guideTab === 'install' && (
                    <div className="guide-panel">
                      <div className="guide-steps">
                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">1</div>
                            <h4 className="step-title">安装 YNPM</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">首先确保你已经安装了 ynpm（5.13.5以上版本） 命令行工具</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('npm install -g ynpm-tool --registry https://registry.npmjs.org/')}>复制</button>
                              <pre><code>npm install -g ynpm-tool --registry https://registry.npmjs.org/</code></pre>
                            </div>
                          </div>
                        </div>

                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">2</div>
                            <h4 className="step-title">搜索并安装技能</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">在技能市场找到需要的技能,使用 add 命令安装</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('ynpm skill add code-reviewer@2.1.0')}>复制</button>
                              <pre><code>{`# 安装最新版本
ynpm skill add code-reviewer

# 安装指定版本(推荐用于生产环境)
ynpm skill add code-reviewer@2.1.0

# 安装到指定目录
ynpm skill add code-reviewer --target yoncode
ynpm skill add code-reviewer -t claude
`}</code></pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {guideTab === 'manage' && (
                    <div className="guide-panel">
                      <div className="guide-steps">
                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">1</div>
                            <h4 className="step-title">更新技能</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">保持技能版本最新,获取最新功能和修复</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('ynpm skill update code-reviewer')}>复制</button>
                              <pre><code>{`# 更新单个技能到最新版本
ynpm skill update code-reviewer

# 更新所有已安装的技能
ynpm skill update

# 更新指定目录中的技能
ynpm skill update code-reviewer --target=yoncode
ynpm skill update code-reviewer -t=claude
`}</code></pre>
                            </div>
                          </div>
                        </div>

                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">2</div>
                            <h4 className="step-title">卸载技能</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">移除不再需要的技能</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('ynpm skill remove code-reviewer')}>复制</button>
                              <pre><code>{`# 卸载单个技能
ynpm skill remove code-reviewer

# 卸载指定目录中的技能
ynpm skill remove code-reviewer --target=yoncode
ynpm skill remove code-reviewer -t=claude
`}</code></pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {guideTab === 'publish' && (
                    <div className="guide-panel">
                      <div className="guide-steps">
                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">1</div>
                            <h4 className="step-title">准备工作</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">确保你的技能包含必要的文件</p>
                            <div className="code-box">
                              <pre><code>{`package.json、
SKILL.md`}</code></pre>
                            </div>
                          </div>
                        </div>

                        <div className="step-item">
                          <div className="step-header">
                            <div className="step-number">2</div>
                            <h4 className="step-title">登录并发布</h4>
                          </div>
                          <div className="step-content">
                            <p className="step-desc">登录你的账号并发布技能到市场</p>
                            <div className="code-box">
                              <button className="copy-btn-small" onClick={() => copyToClipboard('ynpm skill publish')}>复制</button>
                              <pre><code>{`# 登录 ynpm 账号
ynpm login

# 发布技能
ynpm skill publish`}</code></pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Area - Hot Rankings */}
          <div className="content-right">
            <div className="ranking-card">
              <div className="ranking-header">
                <span className="ranking-title">🔥 热门技能排行</span>
              </div>
              <div className="ranking-tabs">
                <button
                  className={`ranking-tab ${rankingTab === 'download' ? 'active' : ''}`}
                  onClick={() => setRankingTab('download')}
                >
                  下载量
                </button>
                <button
                  className={`ranking-tab ${rankingTab === 'active' ? 'active' : ''}`}
                  onClick={() => setRankingTab('active')}
                >
                  激活量
                </button>
              </div>
              <div className="ranking-list">
                {currentRankingData.length > 0 ? (
                  currentRankingData.map((skill, idx) => (
                    <div
                      key={skill.id}
                      className={`ranking-item ${idx < 3 ? 'top-three' : ''}`}
                      onClick={() => window.location.href = `/skill-market/detail/${skill.name}`}
                    >
                      <span className={`ranking-index index-${idx + 1}`}>{idx + 1}</span>
                      <span className="ranking-name" title={skill.name}>{skill.name}</span>
                      <span className="ranking-value">
                        {rankingTab === 'download'
                          ? formatNumber(skill.download || 0)
                          : formatNumber(skill.skill_active_count || 0)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="ranking-empty">暂无数据</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout(SkillMarket, 'skill-market-page');
